// ABOUTME: App-side bridge for an intro-film REPLAY (SPEC-43, R2): raises the first-entry film gate
// ABOUTME: (`showIntro`) around the replay and tells the backend via the existing `video_state` frame.

import { useCallback, useRef } from 'react';
import { flushAgentAudio as flushQueuedAgentAudio } from './agentAudioFlush';

// 1 === WebSocket.OPEN, spelled out because jsdom test doubles do not always carry the constant
// (same as useManualStop's sendStop).
const WS_OPEN = 1;

/**
 * App's queued-audio teardown, the same three moves `useManualStop.flushAndRest` makes for the
 * STOP button: stop the scheduled sources (`agentAudioFlush`), then rest the speaking flags — the
 * flush cancels `speakingTimeoutRef`, and without the resets `isAgentSpeaking` would stay latched
 * until the agent next spoke. The caption/highlight teardown is deliberately NOT repeated here:
 * the film gate already hides that chrome.
 */
function flushAndRest(audio) {
  if (!audio) return;
  flushQueuedAgentAudio({
    audioContext: audio.audioContext,
    activeAudioSourcesRef: audio.activeAudioSourcesRef,
    nextStartTime: audio.nextStartTime,
    speakingTimeoutRef: audio.speakingTimeoutRef,
    resetSpeechClock: audio.resetSpeechClock,
  });
  if (audio.isAgentSpeakingRef) audio.isAgentSpeakingRef.current = false;
  audio.setIsAgentSpeaking?.(false);
  // 'listening' is the transport's resting value (see useManualStop) — never invent a new one.
  audio.setAgentState?.('listening');
}

/**
 * The Projectionist's `useIntroReplay` hook calls `onReplayStart` when it swaps the reel to the
 * film and `onReplayEnd` when the film has ended and the reel is back on idle. This hook turns
 * those two moments into App's existing behaviour:
 *
 *   - `setShowIntro(true/false)` — the FIRST-ENTRY film gate, re-used verbatim. While `showIntro`
 *     is up App drops mic frames, suppresses VAD, drops inbound agent audio and hides the cart /
 *     wishlist chrome, which is exactly what a replayed film wants too.
 *   - `flushAgentAudio?.()` at start — whatever the agent was still saying is silenced before the
 *     film's own soundtrack starts. Optional: the orchestrator passes App's existing flush.
 *   - `{type:'video_state', active}` on the socket — the frame `closeVideoOverlay` already sends;
 *     the backend pauses its stalled-turn watchdog while `active` so a silent model during the
 *     film is not nudged to speak over it.
 *
 * What it deliberately does NOT do: send `intro_finished`. A replay must never produce a second
 * spoken welcome (R2), and the welcome is keyed off that frame — see SPEC-43 D4 / §3.1.
 *
 * Identity: `onStart` / `onEnd` keep ONE identity for the life of the component whatever the
 * caller passes on a given render (latest-ref idiom, as in useThinkingWatchdog). They travel as
 * Projectionist props into a hook whose verbs are captured by App's long-lived `ws.onmessage`
 * closure, so a per-render identity would be a stale-closure bug waiting to happen.
 *
 * Manual test (SPEC-43 R2): on a returning-guest session say "show me the film again". The film
 * starts, the cart/wishlist rail disappears and the mic meter stops moving; the backend log shows
 * `video_state active=true`. When the film ends the reel rests on idle, the rail returns, the log
 * shows `video_state active=false`, and NO welcome is spoken.
 *
 * @param {object}   p
 * @param {{current: (WebSocket|null)}} p.ws  App's socket ref
 * @param {Function} p.setShowIntro            App's `showIntro` setter
 * @param {{current: boolean}} [p.showIntroRef] App's live mirror of `showIntro` — set SYNCHRONOUSLY
 *   here (before the next render), because the mic/VAD/agent-audio gates read the ref, not the
 *   state, and an audio chunk can land between the setState and its effect. First entry sets the
 *   ref the same way.
 * @param {Function} [p.flushAgentAudio]       zero-arg flush of the agent's queued audio, if App has one
 * @param {object}   [p.audio]                 alternatively, App's playback refs + speaking setters —
 *   `{ audioContext, activeAudioSourcesRef, nextStartTime, speakingTimeoutRef, resetSpeechClock,
 *   isAgentSpeakingRef, setIsAgentSpeaking, setAgentState }` (the subset `useManualStop` takes);
 *   the bridge then performs the flush itself so App.js stays a pure pass-through of refs.
 * @returns {{ onStart: Function, onEnd: Function }}
 */
export default function useIntroReplayBridge({
  ws, setShowIntro, showIntroRef, flushAgentAudio, audio,
} = {}) {
  // Live copies, so the stable callbacks always act on the caller's CURRENT setter / flush.
  const latestRef = useRef({ ws, setShowIntro, showIntroRef, flushAgentAudio, audio });
  latestRef.current = { ws, setShowIntro, showIntroRef, flushAgentAudio, audio };

  const send = useCallback((active) => {
    const socket = latestRef.current.ws?.current;
    if (!socket || socket.readyState !== WS_OPEN) return;
    try {
      socket.send(JSON.stringify({ type: 'video_state', active }));
    } catch (e) {
      // A dying socket must not take the film gate with it — the gate is already raised/lowered.
    }
  }, []);

  const onStart = useCallback(() => {
    const { setShowIntro: show, showIntroRef: ref, flushAgentAudio: flush, audio: refs } = latestRef.current;
    if (ref) ref.current = true; // gates read the ref: close them NOW, not after the next commit
    show?.(true);
    if (flush) flush(); else flushAndRest(refs);
    send(true);
  }, [send]);

  const onEnd = useCallback(() => {
    const { setShowIntro: show, showIntroRef: ref } = latestRef.current;
    if (ref) ref.current = false;
    show?.(false);
    send(false);
  }, [send]);

  return { onStart, onEnd };
}

export { useIntroReplayBridge };
